"use client";

import { Settings, ExternalLink } from "lucide-react";
import Link from "next/link";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/components/auth/auth-provider";
import { useQualityFlags, type QualityFlags } from "@/hooks/use-quality-flags";
import { useSearchDefaults, type SearchDefaults } from "@/hooks/use-search-defaults";
import {
  saveSearchDefaults,
  resetSearchDefaults,
  getPipelineStatus,
  type SaveSearchDefaultsPayload,
  type PipelineStatus,
} from "@/lib/api/settings";

import { QualityFlagGroup } from "./quality-flag-controls";

/* ---------- Constants ---------- */

const PANEL_OPEN_KEY = "admin-panel-open";
const DEBUG_SCORES_KEY = "admin-debug-scores";
const QUERY_SUMMARIES_KEY = "admin-query-summaries";
const CALCULATED_LEADERBOARD_KEY = "admin-calculated-leaderboard";

const SCENARIOS = [
  { value: "", label: "No scenario" },
  { value: "error", label: "Error (503)" },
  { value: "slow", label: "Slow response" },
  { value: "empty", label: "Empty results" },
];

/* ---------- Helpers ---------- */

/**
 * Build the save payload from current flags + server defaults.
 *
 * The sidebar only exposes Result Count and Pool Multiplier as live overrides;
 * every other field is sent at its current server default so that saving does
 * NOT wipe persisted precomputed overrides (the backend replaces the whole
 * overrides file with whatever payload it receives).
 */
function buildSavePayload(
  flags: QualityFlags,
  d: SearchDefaults
): SaveSearchDefaultsPayload {
  return {
    search_top_k: flags.search_top_k ?? d.search_top_k,
    candidate_pool_multiplier: flags.candidate_pool_multiplier ?? d.candidate_pool_multiplier,
    search_query_weight: flags.search_query_weight ?? d.search_query_weight,
    search_quality_weight: flags.search_quality_weight ?? d.search_quality_weight,
    linkedin_quality_company_weight: d.linkedin_quality_company_weight,
    github_bonus_weight: d.github_bonus_weight,
    github_only_quality_cap: d.github_only_quality_cap,
    company_tier_weights: d.company_tier_weights,
    school_tier_weights: d.school_tier_weights,
    company_history_decay: d.company_history_decay,
    school_history_decay: d.school_history_decay,
    company_age_decay: d.company_age_decay,
    company_unranked_weight: d.company_unranked_weight,
    school_unranked_weight: d.school_unranked_weight,
  };
}

/** Return human-readable labels for the sidebar settings that differ from server defaults. */
function getChangedLabels(flags: QualityFlags): string[] {
  const changes: string[] = [];
  if (flags.search_top_k != null) changes.push("Result count");
  if (flags.candidate_pool_multiplier != null) changes.push("Pool multiplier");
  return changes;
}

/* ---------- AdminPanel ---------- */

export function AdminPanel() {
  const { isAdmin } = useAuth();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [open, setOpen] = useState(false);
  const [debugScores, setDebugScores] = useState(false);
  const [querySummaries, setQuerySummaries] = useState(true);
  const [calculatedLeaderboard, setCalculatedLeaderboard] = useState(false);
  const [resetHovered, setResetHovered] = useState(false);
  const [toggleHovered, setToggleHovered] = useState(false);

  // Save-as-defaults state
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Reset-to-factory state
  const [showFactoryReset, setShowFactoryReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const { flags, setFlag, resetAll, isDefault } = useQualityFlags();
  const { defaults: serverDefaults, refetch: refetchDefaults } = useSearchDefaults();

  // Hydrate open state and debug scores from localStorage
  useEffect(() => {
    try {
      if (localStorage.getItem(PANEL_OPEN_KEY) === "true") {
        setOpen(true);
      }
      if (localStorage.getItem(DEBUG_SCORES_KEY) === "true") {
        setDebugScores(true);
      }
      const querySummaryToggleRaw = localStorage.getItem(QUERY_SUMMARIES_KEY);
      if (querySummaryToggleRaw === null) {
        // Default-on for all users unless explicitly disabled.
        setQuerySummaries(true);
        localStorage.setItem(QUERY_SUMMARIES_KEY, "true");
      } else {
        setQuerySummaries(querySummaryToggleRaw === "true");
      }
      if (localStorage.getItem(CALCULATED_LEADERBOARD_KEY) === "true") {
        setCalculatedLeaderboard(true);
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  // Persist open state
  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PANEL_OPEN_KEY, String(next));
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  }, []);

  // Close panel on Escape
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setShowConfirm(false);
        setOpen(false);
        try {
          localStorage.setItem(PANEL_OPEN_KEY, "false");
        } catch {
          // localStorage unavailable
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Cleanup pipeline polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Scenario handling (migrated from admin-toolbar.tsx)
  const currentScenario = searchParams.get("scenario") ?? "";

  const handleScenarioChange = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set("scenario", value);
      } else {
        params.delete("scenario");
      }
      const qs = params.toString();
      router.push(qs ? `${pathname}?${qs}` : pathname);
    },
    [searchParams, router, pathname]
  );

  // --- Save as Defaults ---
  const changedLabels = getChangedLabels(flags);

  const handleSaveDefaults = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildSavePayload(flags, serverDefaults);
      const result = await saveSearchDefaults(payload);

      setShowConfirm(false);

      // If pipeline was triggered, defer slider reset until pipeline completes
      if (result.pipeline_triggered) {
        setPipelineStatus({ status: "running", message: "Recomputing LinkedIn quality scores..." });
        pollRef.current = setInterval(async () => {
          try {
            const status = await getPipelineStatus();
            setPipelineStatus(status);
            if (status.status === "completed" || status.status === "failed") {
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
              if (status.status === "completed") {
                // Pipeline done — now reset sliders to new server defaults
                resetAll();
                await refetchDefaults();
                window.dispatchEvent(new Event("quality-flags-apply"));
                setTimeout(() => setPipelineStatus(null), 5000);
              }
            }
          } catch {
            // Ignore poll errors
          }
        }, 3000);
      } else {
        // No pipeline needed — reset sliders immediately
        resetAll();
        await refetchDefaults();
        window.dispatchEvent(new Event("quality-flags-apply"));
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [flags, serverDefaults, resetAll, refetchDefaults]);

  const handleFactoryReset = useCallback(async () => {
    setResetting(true);
    setSaveError(null);
    try {
      const result = await resetSearchDefaults();
      setShowFactoryReset(false);

      if (result.pipeline_triggered) {
        setPipelineStatus({ status: "running", message: "Recomputing LinkedIn quality scores..." });
        pollRef.current = setInterval(async () => {
          try {
            const status = await getPipelineStatus();
            setPipelineStatus(status);
            if (status.status === "completed" || status.status === "failed") {
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
              if (status.status === "completed") {
                resetAll();
                await refetchDefaults();
                window.dispatchEvent(new Event("quality-flags-apply"));
                setTimeout(() => setPipelineStatus(null), 5000);
              }
            }
          } catch {
            // Ignore poll errors
          }
        }, 3000);
      } else {
        resetAll();
        await refetchDefaults();
        window.dispatchEvent(new Event("quality-flags-apply"));
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  }, [resetAll, refetchDefaults]);

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      {/* Toggle button -- always visible for admin */}
      <button
        type="button"
        onClick={toggleOpen}
        onMouseEnter={() => setToggleHovered(true)}
        onMouseLeave={() => setToggleHovered(false)}
        aria-label={open ? "Close admin sidebar" : "Open admin sidebar"}
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          background: toggleHovered
            ? "color-mix(in srgb, var(--surface) 90%, #000 10%)"
            : "color-mix(in srgb, var(--surface) 95%, #000 5%)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          color: toggleHovered ? "var(--text-secondary)" : "var(--text-muted)",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.04em",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          transition: "background 150ms ease, color 150ms ease",
        }}
      >
        <Settings size={14} />
        Admin
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={toggleOpen}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 49,
            background: "rgba(0, 0, 0, 0.15)",
            animation: "adminBackdropFadeIn 200ms ease forwards",
          }}
        />
      )}

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 320,
          zIndex: 50,
          background: "var(--white)",
          borderLeft: "1px solid var(--border)",
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.08)",
          overflowY: "auto",
          padding: 24,
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 250ms cubic-bezier(0.22, 1, 0.36, 1)",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        {/* Pipeline status banner */}
        {pipelineStatus && (
          <div
            style={{
              marginBottom: 16,
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background:
                pipelineStatus.status === "running"
                  ? "color-mix(in srgb, var(--accent-blue) 10%, transparent)"
                  : pipelineStatus.status === "completed"
                    ? "color-mix(in srgb, #22c55e 10%, transparent)"
                    : "color-mix(in srgb, #ef4444 10%, transparent)",
              color:
                pipelineStatus.status === "running"
                  ? "var(--accent-blue)"
                  : pipelineStatus.status === "completed"
                    ? "#16a34a"
                    : "#dc2626",
              border: `1px solid ${
                pipelineStatus.status === "running"
                  ? "color-mix(in srgb, var(--accent-blue) 20%, transparent)"
                  : pipelineStatus.status === "completed"
                    ? "color-mix(in srgb, #22c55e 20%, transparent)"
                    : "color-mix(in srgb, #ef4444 20%, transparent)"
              }`,
            }}
          >
            {pipelineStatus.status === "running" && (
              <span style={{ marginRight: 6, display: "inline-block", animation: "spin 1s linear infinite" }}>
                &#9696;
              </span>
            )}
            {pipelineStatus.message}
            {pipelineStatus.elapsed_seconds != null && pipelineStatus.status === "running" && (
              <span style={{ opacity: 0.7, marginLeft: 6 }}>
                ({Math.round(pipelineStatus.elapsed_seconds)}s)
              </span>
            )}
            {pipelineStatus.status !== "running" && (
              <button
                type="button"
                onClick={() => setPipelineStatus(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  float: "right",
                  fontSize: 11,
                  opacity: 0.6,
                  color: "inherit",
                }}
              >
                dismiss
              </button>
            )}
          </div>
        )}

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
            Admin Sidebar
          </span>
          <button
            type="button"
            onClick={() => {
              resetAll();
              window.dispatchEvent(new Event("quality-flags-apply"));
            }}
            onMouseEnter={() => setResetHovered(true)}
            onMouseLeave={() => setResetHovered(false)}
            disabled={isDefault}
            style={{
              background: "none",
              border: "none",
              fontSize: 11,
              fontWeight: 600,
              color: isDefault
                ? "var(--muted)"
                : resetHovered
                  ? "var(--text)"
                  : "var(--muted)",
              cursor: isDefault ? "default" : "pointer",
              textDecoration: resetHovered && !isDefault ? "underline" : "none",
              opacity: isDefault ? 0.5 : 1,
              padding: 0,
              transition: "color 150ms ease",
            }}
          >
            Reset to defaults
          </button>
        </div>

        {/* Quality Flags section */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "var(--text-muted)",
            marginBottom: 8,
          }}
        >
          SEARCH POOL
        </div>
        <QualityFlagGroup flags={flags} setFlag={setFlag} serverDefaults={serverDefaults} />

        {/* Save as Defaults button */}
        {!isDefault && !showConfirm && (
          <div style={{ padding: "12px 0 4px" }}>
            <button
              type="button"
              onClick={() => { setShowConfirm(true); setSaveError(null); }}
              style={{
                width: "100%",
                padding: "8px 0",
                background: "var(--text)",
                color: "var(--white)",
                border: "none",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.02em",
              }}
            >
              Save as server defaults
            </button>
          </div>
        )}

        {/* Confirmation dialog */}
        {showConfirm && (
          <div
            style={{
              margin: "12px 0 4px",
              padding: 12,
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--surface-elevated)",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "var(--text)" }}>
              Save as server defaults?
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 8, lineHeight: 1.5 }}>
              This will update defaults for all users.
              {changedLabels.length > 0 && (
                <>
                  {" "}Changed settings:
                  <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                    {changedLabels.map((label) => (
                      <li key={label} style={{ marginBottom: 2 }}>
                        {label}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            {saveError && (
              <div style={{ fontSize: 10, color: "#ef4444", marginBottom: 8 }}>
                {saveError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "var(--text-secondary)",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveDefaults}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  background: saving ? "var(--muted)" : "var(--text)",
                  color: "var(--white)",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: saving ? "default" : "pointer",
                }}
              >
                {saving ? "Saving..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        )}

        {/* Reset to factory defaults */}
        {isDefault && !showFactoryReset && (
          <div style={{ padding: "4px 0" }}>
            <button
              type="button"
              onClick={() => { setShowFactoryReset(true); setSaveError(null); }}
              style={{
                width: "100%",
                padding: "6px 0",
                background: "none",
                color: "var(--text-muted)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.02em",
              }}
            >
              Reset server to factory defaults
            </button>
          </div>
        )}

        {showFactoryReset && (
          <div
            style={{
              margin: "8px 0 4px",
              padding: 12,
              borderRadius: 8,
              border: "1px solid color-mix(in srgb, #ef4444 30%, var(--border))",
              background: "color-mix(in srgb, #ef4444 4%, var(--surface-elevated))",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "var(--text)" }}>
              Reset to factory defaults?
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 8, lineHeight: 1.5 }}>
              This will erase all saved server defaults and revert to config.py values. All users will be affected.
            </div>
            {saveError && (
              <div style={{ fontSize: 10, color: "#ef4444", marginBottom: 8 }}>
                {saveError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowFactoryReset(false)}
                disabled={resetting}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "var(--text-secondary)",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFactoryReset}
                disabled={resetting}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  background: resetting ? "var(--muted)" : "#dc2626",
                  color: "var(--white)",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: resetting ? "default" : "pointer",
                }}
              >
                {resetting ? "Resetting..." : "Confirm Reset"}
              </button>
            </div>
          </div>
        )}

        {/* Divider */}
        <div
          style={{ height: 1, background: "var(--border)", margin: "16px 0" }}
        />

        {/* Stacks section */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "var(--text-muted)",
            marginBottom: 8,
          }}
        >
          STACKS
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            userSelect: "none",
            padding: "8px 0",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          <input
            type="checkbox"
            checked={calculatedLeaderboard}
            onChange={(e) => {
              setCalculatedLeaderboard(e.target.checked);
              try {
                localStorage.setItem(
                  CALCULATED_LEADERBOARD_KEY,
                  String(e.target.checked)
                );
                // Clear stacks sessionStorage cache so fresh data is fetched
                sessionStorage.removeItem("stacks_cache_v2");
                window.dispatchEvent(new Event("calculated-leaderboard-changed"));
              } catch {
                /* */
              }
            }}
            style={{ accentColor: "var(--accent-blue)" }}
          />
          Calculated leaderboard
        </label>

        {/* Divider */}
        <div
          style={{ height: 1, background: "var(--border)", margin: "16px 0" }}
        />

        {/* Debug section */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "var(--text-muted)",
            marginBottom: 8,
          }}
        >
          DEBUG
        </div>

        {/* Debug scores checkbox */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            userSelect: "none",
            padding: "8px 0",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          <input
            type="checkbox"
            checked={debugScores}
            onChange={(e) => {
              setDebugScores(e.target.checked);
              try {
                localStorage.setItem(
                  DEBUG_SCORES_KEY,
                  String(e.target.checked)
                );
                window.dispatchEvent(new Event("debug-scores-changed"));
              } catch {
                /* */
              }
            }}
            style={{ accentColor: "var(--accent-blue)" }}
          />
          Score debug
        </label>

        {/* Query-aware summaries toggle */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            userSelect: "none",
            padding: "8px 0",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          <input
            type="checkbox"
            checked={querySummaries}
            onChange={(e) => {
              setQuerySummaries(e.target.checked);
              try {
                localStorage.setItem(
                  QUERY_SUMMARIES_KEY,
                  String(e.target.checked)
                );
                window.dispatchEvent(new Event("query-summaries-changed"));
                // Trigger re-search so the user sees the difference
                window.dispatchEvent(new Event("quality-flags-apply"));
              } catch {
                /* */
              }
            }}
            style={{ accentColor: "var(--accent-blue)" }}
          />
          Query-aware summaries
        </label>

        {/* Scenario selector */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 0",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--text-secondary)",
          }}
        >
          Scenario
          <select
            value={currentScenario}
            onChange={(e) => handleScenarioChange(e.target.value)}
            style={{
              background: "var(--surface-elevated)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              color: "var(--text-primary)",
              padding: "0.2rem 0.4rem",
              fontSize: 11,
            }}
          >
            {SCENARIOS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        {/* Admin dashboard link */}
        <div style={{ padding: "8px 0" }}>
          <Link
            href="/admin"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "var(--accent-blue)",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            Admin Dashboard
            <ExternalLink size={11} />
          </Link>
        </div>
      </div>

      {/* Inline keyframes for backdrop fade-in + spinner */}
      <style>{`
        @keyframes adminBackdropFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 767px) {
          div[style*="width: 320"] {
            width: min(320px, calc(100vw - 16px)) !important;
          }
        }
      `}</style>
    </>
  );
}
